import React, { useState } from 'react';
import { Modal, Upload, Button, Image, message, Spin } from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import './PhotoUploadModal.css';

const PhotoUploadModal = ({ visible, onClose, onUpload, boxNumber, inventoryId }) => {
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Handle file selection
  const handleFileChange = (info) => {
    const file = info.file.originFileObj || info.file;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Remove selected image
  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setPreviewUrl(null);
  };

  // Submit photo upload
  const handleSubmit = async () => {
    if (!imageFile) {
      message.error('Please select a photo first');
      return;
    }

    setUploading(true);
    try {
      await onUpload(inventoryId, imageFile);
      message.success('Photo uploaded successfully!');
      handleRemoveImage();
      onClose();
    } catch (error) {
      message.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    handleRemoveImage();
    onClose();
  };

  return (
    <Modal
      title={`Upload Photo for Box ${boxNumber}`}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleSubmit}
          loading={uploading}
          disabled={!imageFile}
          className="bg-theme-secondary custom-upload-button"
        >
          Upload Photo
        </Button>
      ]}
      width={500}
      centered
    >
      <div className="photo-upload-container">
        <div className="upload-instructions">
          <p className="mb-2">
            Take an overhead photo of your box contents before sealing the box.
          </p>
          <p className="text-muted small">
            Supported formats: JPG, PNG, GIF
          </p>
        </div>

        {!previewUrl ? (
          <div className="upload-area">
            <Upload
              accept="image/*"
              capture="environment"
              showUploadList={false}
              onChange={handleFileChange}
              beforeUpload={() => false}
              maxCount={1}
            >
              <div className="upload-button-wrapper">
                <Button
                  type="default"
                  size="large"
                  icon={<UploadOutlined />}
                  className="upload-button"
                  block
                >
                  Choose from Gallery
                </Button>
              </div>
            </Upload>
          </div>
        ) : (
          <div className="preview-area">
            <div className="preview-header">
              <h4>Preview</h4>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleRemoveImage}
                disabled={uploading}
              />
            </div>
            <div className="image-preview">
              <Image
                src={previewUrl}
                alt="Box content preview"
                className="preview-image"
                preview={false}
              />
            </div>
            {uploading && (
              <div className="uploading-overlay">
                <Spin size="large" tip="Uploading photo..." />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

PhotoUploadModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  boxNumber: PropTypes.string.isRequired,
  inventoryId: PropTypes.number.isRequired
};

export default PhotoUploadModal;
